<script setup>
import { computed } from 'vue';
import { useRoute, RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

const { t } = useI18n();
const route = useRoute();
const { isOnChatwootCloud } = useAccount();

const assistantParams = computed(() => ({
  accountId: route.params.accountId,
  assistantId: route.params.assistantId,
}));

const links = computed(() => {
  const items = [
    {
      key: 'playground',
      title: t('ARROWS_AI.OVERVIEW.LINKS.PLAYGROUND.TITLE'),
      description: t('ARROWS_AI.OVERVIEW.LINKS.PLAYGROUND.DESCRIPTION'),
      icon: 'i-lucide-flask-conical',
      to: {
        name: 'arrows_ai_assistants_playground',
        params: assistantParams.value,
      },
    },
    {
      key: 'documents',
      title: t('ARROWS_AI.OVERVIEW.LINKS.DOCUMENTS.TITLE'),
      description: t('ARROWS_AI.OVERVIEW.LINKS.DOCUMENTS.DESCRIPTION'),
      icon: 'i-lucide-file-text',
      to: {
        name: 'arrows_ai_assistants_documents',
        params: assistantParams.value,
      },
    },
    {
      key: 'resources',
      title: t('ARROWS_AI.OVERVIEW.LINKS.RESOURCES.TITLE'),
      description: t('ARROWS_AI.OVERVIEW.LINKS.RESOURCES.DESCRIPTION'),
      icon: 'i-lucide-images',
      to: {
        name: 'arrows_ai_assistants_resources',
        params: assistantParams.value,
      },
    },
    {
      key: 'services',
      title: t('ARROWS_AI.OVERVIEW.LINKS.SERVICES.TITLE'),
      description: t('ARROWS_AI.OVERVIEW.LINKS.SERVICES.DESCRIPTION'),
      icon: 'i-lucide-briefcase',
      to: {
        name: 'arrows_ai_assistants_services',
        params: assistantParams.value,
      },
    },
    {
      key: 'tools',
      title: t('ARROWS_AI.OVERVIEW.LINKS.TOOLS.TITLE'),
      description: t('ARROWS_AI.OVERVIEW.LINKS.TOOLS.DESCRIPTION'),
      icon: 'i-lucide-wrench',
      to: {
        name: 'arrows_ai_assistants_tools',
        params: assistantParams.value,
      },
    },
    {
      key: 'settings',
      title: t('ARROWS_AI.OVERVIEW.LINKS.SETTINGS.TITLE'),
      description: t('ARROWS_AI.OVERVIEW.LINKS.SETTINGS.DESCRIPTION'),
      icon: 'i-lucide-settings',
      to: {
        name: 'arrows_ai_assistants_settings',
        params: assistantParams.value,
      },
    },
  ];

  if (isOnChatwootCloud.value) {
    items.push({
      key: 'billing',
      title: t('ARROWS_AI.OVERVIEW.LINKS.BILLING.TITLE'),
      description: t('ARROWS_AI.OVERVIEW.LINKS.BILLING.DESCRIPTION'),
      icon: 'i-lucide-credit-card',
      to: {
        name: 'billing_settings_index',
        params: { accountId: route.params.accountId },
      },
    });
  }

  return items;
});
</script>

<template>
  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
    <RouterLink
      v-for="link in links"
      :key="link.key"
      :to="link.to"
      class="flex items-center gap-3 p-4 transition-colors border rounded-xl bg-n-solid-1 border-n-weak hover:bg-n-alpha-1 group/link"
    >
      <span
        class="grid rounded-lg size-9 shrink-0 place-content-center bg-n-alpha-2 text-n-slate-11"
      >
        <span :class="link.icon" class="size-4" />
      </span>
      <div class="flex flex-col min-w-0">
        <span class="text-sm font-medium text-n-slate-12">{{
          link.title
        }}</span>
        <span class="text-xs truncate text-n-slate-11">
          {{ link.description }}
        </span>
      </div>
      <span
        class="ml-auto transition-opacity opacity-0 i-lucide-chevron-right size-4 text-n-slate-10 group-hover/link:opacity-100"
      />
    </RouterLink>
  </div>
</template>
